import styled from "styled-components";
import {radii, shadows} from "theme/tokens";

/**
 * Shared presentational atoms for the row-action overflow (⋮) menus (ui#283). The transfers file row
 * ({@link ../../transfers/contacts/TransferListItem}) and the reminders row
 * ({@link ../../reminders/reminders/ReminderItem}) render the same v3 menu surface + labelled item
 * pattern, so it lives here once instead of being copy-pasted. Every colour comes from `theme.tokens`.
 */

/**
 * Shared ⋮ trigger button (ui#286) — the 40px `field`-plate square that opens a row-action menu,
 * consumed by the contact row card, the group card, and the transfer and file rows of the Transfers
 * screen (ui#3403: those rows used to borrow the recipient page's ringed download button, so the ring
 * followed the download page's design instead of the row menus'). A quiet plate that warms to the
 * `line` surface on hover; the accessible name lives on the caller's `aria-label` (the
 * {@link ./rowMenu.DotsGlyph} inside is decorative).
 */
export const MenuTrigger = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	padding: 0;
	border: 0;
	flex: none;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	transition: background 0.14s, color 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Icon + label row inside a row-action `MenuItem`. */
export const MenuItemLabel = styled.span`
	display: flex;
	align-items: center;
	gap: 10px;
`;

/** Consistent `MenuItem` sizing/typography for the overflow menus. Spread into MUI `sx`. */
export const rowMenuItemSx = {gap: "10px", fontSize: "13.5px", fontWeight: 600, px: "14px", py: "9px"};

/**
 * MUI Menu `slotProps` giving the v3 popover surface: rounded control radius, the token card border,
 * and the shared popover shadow. `cardBorder` is passed in — it is a per-mode token read from the
 * active theme by the caller (`useTheme().tokens.cardBorder`), so nothing is hardcoded here.
 */
export function rowMenuPaperSlotProps(cardBorder: string) {
	return {
		paper: {
			style: {
				borderRadius: radii.control,
				border: `1px solid ${cardBorder}`,
				boxShadow: shadows.popover,
				minWidth: 176,
			},
		},
	};
}
